/**
 * Data access for the public directory. Its endpoints are new, so they aren't
 * in the generated schema.d.ts yet; these call them through the shared client
 * (`request`), which works signed out too (no token is attached then).
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { request } from "@/api/client";

import type { DirectoryKind } from "./kinds";
import type {
  DirectorySearch,
  PlaceCount,
  RaProfile,
  RaSearch,
  StateCount,
} from "./types";

export const PAGE_SIZE = 20;

export function searchQuery(s: DirectorySearch, kind: DirectoryKind = "ra") {
  const near = s.lat !== undefined && s.lon !== undefined;
  return {
    q: s.q || undefined,
    city: s.city || undefined,
    state: s.state || undefined,
    pincode: near ? undefined : s.pincode || undefined,
    lat: near ? s.lat : undefined,
    lon: near ? s.lon : undefined,
    radius_km: (near || s.pincode) && s.radius ? s.radius : undefined,
    // only RAs publish on the platform
    on_platform: s.platform && kind === "ra" ? true : undefined,
    kind: kind === "ra" ? undefined : kind,
    page: s.page && s.page > 1 ? s.page : undefined,
    page_size: PAGE_SIZE,
  };
}

export function useRaSearch(s: DirectorySearch, kind: DirectoryKind = "ra") {
  const query = searchQuery(s, kind);
  return useQuery({
    queryKey: ["/directory/ras", null, query] as const,
    queryFn: ({ signal }) =>
      request<RaSearch>("get", "/directory/ras", { query, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useStates(kind: DirectoryKind = "ra") {
  const query = kind === "ra" ? null : { kind };
  return useQuery({
    queryKey: ["/directory/states", null, query] as const,
    queryFn: ({ signal }) =>
      request<StateCount[]>("get", "/directory/states", {
        query: query ?? undefined,
        signal,
      }),
    staleTime: 60 * 60 * 1000,
  });
}

/** The register's states with their cities, for every state and city picker. */
export function usePlaces(kind: DirectoryKind = "ra") {
  return useQuery({
    queryKey: ["/directory/places", null, { kind }] as const,
    queryFn: ({ signal }) =>
      request<PlaceCount[]>("get", "/directory/places", {
        query: { kind },
        signal,
      }),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * The state and city choices from the register: every state, and the chosen state's
 * cities (or, with no state, the biggest cities anywhere). A city typed in an old
 * link that the register doesn't spell that way is kept as a choice.
 */
export function placeOptions(
  places: PlaceCount[] | undefined,
  state: string | undefined,
  city: string,
  all: string,
) {
  const list = places ?? [];
  const states = [
    { value: all, label: "All states" },
    ...list.map((p) => ({ value: p.state, label: `${p.state} (${p.n})` })),
  ];
  const inState =
    state && state !== all
      ? list.find((p) => p.state === state)?.cities
      : undefined;
  let cities = inState;
  if (!cities) {
    const merged = new Map<string, number>();
    for (const p of list)
      for (const c of p.cities)
        merged.set(c.city, (merged.get(c.city) ?? 0) + c.n);
    cities = [...merged]
      .map(([c, n]) => ({ city: c, n }))
      .sort((a, b) => b.n - a.n || a.city.localeCompare(b.city));
  }
  const cityOpts = [
    { value: all, label: "All cities" },
    ...cities.map((c) => ({ value: c.city, label: `${c.city} (${c.n})` })),
  ];
  if (
    city &&
    !cityOpts.some((o) => o.value.toLowerCase() === city.toLowerCase())
  )
    cityOpts.push({ value: city, label: city });
  const cityValue = city
    ? (cityOpts.find((o) => o.value.toLowerCase() === city.toLowerCase())
        ?.value ?? city)
    : all;
  return { states, cities: cityOpts, cityValue };
}

export function useRaProfile(regNo: string) {
  return useQuery({
    queryKey: ["/directory/ras/{reg_no}", { reg_no: regNo }, null] as const,
    queryFn: ({ signal }) =>
      request<RaProfile>("get", "/directory/ras/{reg_no}", {
        path: { reg_no: regNo },
        signal,
      }),
  });
}

/** "INH000012345" (RA) or "INA000012345" (IA) check, same rule as the API. */
export const REG_NO = /^IN[AH]\d{9}$/i;

export function parseDirectorySearch(
  s: Record<string, unknown>,
): DirectorySearch {
  const str = (v: unknown, max = 100) =>
    typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined;
  const num = (v: unknown) => {
    const n =
      typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const pin = str(s.pincode, 6);
  const page = num(s.page);
  const radius = num(s.radius);
  const lat = num(s.lat);
  const lon = num(s.lon);
  const out: DirectorySearch = {};
  const q = str(s.q);
  const city = str(s.city, 60);
  const state = str(s.state, 60);
  if (q) out.q = q;
  if (city) out.city = city;
  if (state) out.state = state;
  if (pin && /^[1-9]\d{5}$/.test(pin)) out.pincode = pin;
  if (radius && radius > 0 && radius <= 2000) out.radius = radius;
  if (
    lat !== undefined &&
    lon !== undefined &&
    lat >= 6 &&
    lat <= 38 &&
    lon >= 68 &&
    lon <= 98
  ) {
    out.lat = Math.round(lat * 1000) / 1000;
    out.lon = Math.round(lon * 1000) / 1000;
  }
  if (s.platform === true || s.platform === "true" || s.platform === "1")
    out.platform = true;
  if (page && page > 1) out.page = Math.floor(page);
  return out;
}
